import { contactsService } from "@/lib/services";
import type { Contact } from "./contract";
export type SignerSyncResult = {
  action: "created" | "updated" | "existing" | "skipped";
  contact_id: string | null;
  note: string | null;
};
export async function syncSignerContact(input: {
  locationId: string;
  name: string | null | undefined;
  email: string | null | undefined;
  role?: string | null;
}): Promise<SignerSyncResult> {
  const rows: Contact[] = await contactsService.getByLocationId(
    input.locationId,
  );
  const name = input.name?.trim() || "";
  const email = input.email?.trim().toLowerCase() || "";
  const skip = (note: string): SignerSyncResult => ({
    action: "skipped",
    contact_id: null,
    note,
  });
  if (rows.some((row) => !row.can_edit))
    return skip("Contact sync requires reviewed identity workflow.");
  if (!name || !email || !/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email))
    return skip("Add a contact manually with a confirmed name and email.");
  const sameName = rows.filter(
    (row) => row.name.trim().toLowerCase() === name.toLowerCase(),
  );
  const matches = sameName.filter(
    (row) => row.email.trim().toLowerCase() === email,
  );
  if (matches.length > 1 || (sameName.length > 0 && matches.length === 0))
    return skip(
      "Contact details differ or have duplicate matches. Review location contacts.",
    );
  if (matches.length === 1) {
    const row = matches[0]!;
    if (!input.role?.trim() || row.role === input.role.trim())
      return { action: "existing", contact_id: row.id, note: null };
    const detail = await contactsService.getById(row.id);
    if (
      !detail?.can_edit ||
      detail.email !== row.email ||
      detail.name !== row.name ||
      detail.updated_at !== row.updated_at
    )
      return skip("Contact changed. Review location contacts.");
    const saved = await contactsService.update(
      detail.id,
      { role: input.role.trim() },
      row.updated_at,
    );
    return { action: "updated", contact_id: saved.id, note: null };
  }
  const saved = await contactsService.create({
    location_id: input.locationId,
    name,
    email,
    role: input.role?.trim() || null,
    is_primary: rows.length === 0,
  });
  return {
    action: saved.mutation_outcome === "existing" ? "existing" : "created",
    contact_id: saved.id,
    note: null,
  };
}
